/** Autoservicio: el usuario de staff cambia su propia clave (verifica la actual). */
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import { KeyRound } from "lucide-react";

import { useToast } from "../../components/Toast";
import { Button, Card, Input, PageHeader } from "../../components/ui";
import { ApiError } from "../../lib/api";
import { cambiarMiClave } from "./api";

const schema = z
  .object({
    password_actual: z.string().min(1, "Ingresa tu clave actual"),
    password_nueva: z.string().min(8, "Mínimo 8 caracteres").max(128),
    repetir: z.string(),
  })
  .refine((v) => v.password_nueva === v.repetir, {
    message: "Las claves no coinciden",
    path: ["repetir"],
  });

type Values = z.infer<typeof schema>;

export function MiClavePage(): JSX.Element {
  const toast = useToast();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { password_actual: "", password_nueva: "", repetir: "" },
  });

  const mut = useMutation({
    mutationFn: (v: Values) => cambiarMiClave(v.password_actual, v.password_nueva),
    onSuccess: () => {
      toast.success("Clave actualizada");
      reset();
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : "No se pudo cambiar la clave"),
  });

  return (
    <div className="mx-auto max-w-md space-y-6">
      <PageHeader
        title="Cambiar mi clave"
        icon={KeyRound}
        subtitle="Mínimo 8 caracteres. No puede ser tu usuario, tu correo ni una clave común."
      />

      <Card padding="lg">
        <form onSubmit={handleSubmit((v) => mut.mutate(v))} className="space-y-4">
          <Input
            label="Clave actual"
            type="password"
            autoComplete="current-password"
            {...register("password_actual")}
            error={errors.password_actual?.message}
          />
          <Input
            label="Clave nueva"
            type="password"
            autoComplete="new-password"
            {...register("password_nueva")}
            error={errors.password_nueva?.message}
          />
          <Input
            label="Repetir clave nueva"
            type="password"
            autoComplete="new-password"
            {...register("repetir")}
            error={errors.repetir?.message}
          />
          <div className="flex justify-end">
            <Button type="submit" disabled={mut.isPending}>
              {mut.isPending ? "Guardando…" : "Guardar clave"}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
